import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import AnnouncementCard from './components/AnnouncementCard';
import ProgressionUpdateCard from './components/ProgressionUpdateCard';
import QuickAccessCard from './components/QuickAccessCard';
import AnnouncementFilters from './components/AnnouncementFilters';
import TrendingContentCard from './components/TrendingContentCard';

const GuildDashboard = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const mockAnnouncements = [
    {
      id: 1,
      title: "Buried Sea Raid Schedule - Week of February 23rd",
      author: "Thaldrin Ironforge",
      authorAvatar: "T",
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      priority: "high",
      isRead: false,
      content: "Guild members, we're organizing our weekly Buried Sea progression raids starting this Thursday at 8 PM EST. We'll be focusing on Solteris and Crystallos instances. Please ensure your characters are properly geared and attuned. Bring your best consumables and be ready for challenging encounters. Officers will be reviewing attendance and performance for future raid assignments.",
      tags: ["Raids", "Buried Sea", "Schedule"],
      type: "raids"
    },
    {
      id: 2,
      title: "New Recruitment Drive for Clerics and Enchanters",
      author: "Mystara Lightbringer",
      authorAvatar: "M",
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
      priority: "medium",
      isRead: false,
      content: "Maladapted Miscreants is actively seeking skilled Clerics and Enchanters to join our progression team. We\'re looking for players who are committed to raiding 3-4 nights per week and have experience with current expansion content. Applicants should have at least level 75 characters with appropriate AA development. Contact any officer for application details.",
      tags: ["Recruitment", "Clerics", "Enchanters"],
      type: "recruitment"
    },
    {
      id: 3,
      title: "Guild Bank Reorganization Complete",
      author: "Shadowbane Darkwhisper",
      authorAvatar: "S",
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
      priority: "low",
      isRead: true,
      content: "The guild bank has been reorganized with new tabs for Buried Sea materials and raid consumables. Members with appropriate permissions can now access crafting materials more efficiently. Please remember to follow withdrawal limits and contribute excess materials when possible. Tab 5 is now dedicated to Solteris attunement items.",
      tags: ["Updates", "Guild Bank"],
      type: "updates"
    },
    {
      id: 4,
      title: "Congratulations on Solteris First Clear!",
      author: "Firewing Embercaster",
      authorAvatar: "F",
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      priority: "high",
      isRead: true,
      content: "Incredible work team! Last night we achieved our first full clear of Solteris, marking a major milestone in our Buried Sea progression. Special recognition goes to our main tank team for flawless execution and our healers for keeping everyone alive through the final encounter. Loot distribution will be handled according to our DKP system. Screenshots and combat logs have been posted in the guild forums.",
      tags: ["Raids", "Achievement", "Solteris"],
      type: "raids"
    },
    {
      id: 5,
      title: "Weekly Guild Meeting - Sunday 7 PM EST",
      author: "Thaldrin Ironforge",
      authorAvatar: "T",
      timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000),
      priority: "medium",
      isRead: true,
      content: "All guild members are invited to our weekly meeting this Sunday at 7 PM EST in the guild hall. We'll be discussing upcoming raid strategies, reviewing progression goals, and addressing member feedback. Officers will present plans for the next month's content schedule. Your attendance and input are valued as we continue building our community.",
      tags: ["Events", "Meeting"],
      type: "events"
    },
    {
      id: 6,
      title: "Updated DKP System and Loot Rules",
      author: "Mystara Lightbringer",
      authorAvatar: "M",
      timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000),
      priority: "high",
      isRead: true,
      content: "We've implemented updates to our DKP system based on member feedback. The new system includes bonus points for progression kills, attendance tracking improvements, and clearer loot priority guidelines. All members should review the updated rules posted in the guild forums. The changes take effect immediately and will apply to all future raids. Questions can be directed to any officer.",
      tags: ["Updates", "DKP", "Loot Rules"],
      type: "updates"
    }
  ];

  const mockProgressionUpdates = [
    {
      id: 1,
      characterName: "Thaldrin",
      achievement: "Completed Solteris: The Throne of Ro",
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      type: "boss",
      contentArea: "Solteris",
      progress: 100
    },
    {
      id: 2,
      characterName: "Mystara",
      achievement: "Obtained Crystallos Attunement",
      timestamp: new Date(Date.now() - 90 * 60 * 1000),
      type: "quest",
      contentArea: "Crystallos",
      progress: 75
    },
    {
      id: 3,
      characterName: "Shadowbane",
      achievement: "Defeated Praetor Vitio in Katta Castrum",
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      type: "boss",
      contentArea: "Katta Castrum",
      progress: 85
    },
    {
      id: 4,
      characterName: "Firewing",
      achievement: "Completed Buried Sea Epic 2.0 Quest",
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      type: "milestone",
      contentArea: "Epic Quests",
      progress: 100
    },
    {
      id: 5,
      characterName: "Healara",
      achievement: "Reached 1000 AA Points",
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
      type: "milestone",
      contentArea: "Character Development",
      progress: 68
    }
  ];

  const mockTrendingContent = [
    {
      id: 1,
      name: "Solteris Progression",
      completions: 18,
      total: 25,
      trend: 15,
      activeMembers: 12,
      avgTime: "2.5h"
    },
    {
      id: 2,
      name: "Crystallos Attunement",
      completions: 22,
      total: 25,
      trend: 8,
      activeMembers: 8,
      avgTime: "45m"
    },
    {
      id: 3,
      name: "Katta Castrum Raids",
      completions: 15,
      total: 25,
      trend: 12,
      activeMembers: 15,
      avgTime: "1.8h"
    }
  ];

  useEffect(() => {
    setAnnouncements(mockAnnouncements);
  }, []);

  const filteredAnnouncements = announcements?.filter(announcement => 
    activeFilter === 'all' || announcement?.type === activeFilter
  );

  const handleLoadMore = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-12 px-4 md:px-6 lg:px-8">
        <div className="max-w-screen-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
              Guild Dashboard
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Welcome back to Maladapted Miscreants. Stay updated with guild activities and progression.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="card">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                    Guild Announcements
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {filteredAnnouncements?.filter(a => !a?.isRead)?.length} unread
                    </span>
                  </div>
                </div>

                <AnnouncementFilters 
                  activeFilter={activeFilter}
                  onFilterChange={setActiveFilter}
                />

                <div className="space-y-4">
                  {filteredAnnouncements?.length > 0 ? (
                    filteredAnnouncements?.map((announcement) => (
                      <AnnouncementCard key={announcement?.id} announcement={announcement} />
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <Icon name="MessageSquare" size={48} className="mx-auto mb-4 text-muted-foreground" />
                      <p className="text-muted-foreground">No announcements found for this filter.</p>
                    </div>
                  )}
                </div>

                {filteredAnnouncements?.length > 0 && (
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoading}
                    className="w-full mt-6 px-6 py-3 rounded-xl bg-muted text-foreground font-medium hover:bg-card transition-base flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <Icon name="Loader2" size={20} className="animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Icon name="ChevronDown" size={20} />
                        Load More Announcements
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="card">
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-6">
                  Recent Progression
                </h2>
                <div className="space-y-3">
                  {mockProgressionUpdates?.map((update) => (
                    <ProgressionUpdateCard key={update?.id} update={update} />
                  ))}
                </div>
              </div>

              <div className="card">
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-6">
                  Trending Content
                </h2>
                <div className="space-y-4">
                  {mockTrendingContent?.map((content) => (
                    <TrendingContentCard key={content?.id} content={content} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            <QuickAccessCard
              title="Expansion Progression"
              description="Track Buried Sea content completion and view detailed progression checklists"
              icon="TrendingUp"
              route="/expansion-progression"
              progress={72}
              stats={[
                { icon: 'CheckCircle2', value: '18', label: 'Completed' },
                { icon: 'Circle', value: '7', label: 'In Progress' }
              ]}
            />
            <QuickAccessCard
              title="Character Management"
              description="Manage your character profiles and track individual progression"
              icon="Users"
              route="/character-management"
              progress={0}
              stats={[
                { icon: 'User', value: '4', label: 'Characters' },
                { icon: 'Star', value: '856', label: 'Total AA' }
              ]}
            />
            <QuickAccessCard
              title="Member Directory"
              description="Browse guild roster and view member character associations"
              icon="UserCircle"
              route="/member-directory"
              progress={0}
              stats={[
                { icon: 'Users', value: '25', label: 'Members' },
                { icon: 'Shield', value: '5', label: 'Officers' }
              ]}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default GuildDashboard;